import { api, errorMessage } from '@/lib/api';
import { toast } from '@/lib/toast';
import { fromMinorUnits, toMinorUnits } from '@/lib/money';

type Raw = Record<string, unknown>;
const str = (v: unknown) => (v == null ? '' : String(v));
const reservationIds = new Map<number, string>();

function legacyId(uuid: string): number {
  let hash = 0;
  for (let i = 0; i < uuid.length; i += 1) hash = ((hash << 5) - hash + uuid.charCodeAt(i)) | 0;
  const id = Math.abs(hash) || 1;
  reservationIds.set(id, uuid);
  return id;
}

function reservationIdFor(bookingId: number): string {
  const id = reservationIds.get(bookingId);
  if (!id) throw new Error('Reservation identifier is unavailable. Refresh the reservation list and try again.');
  return id;
}

/** Backend ReservationStatus (snake) → the UI's hyphenated status. */
function mapReservationStatus(s: unknown): string {
  const v = str(s);
  if (v === 'checked_in') return 'checked-in';
  if (v === 'checked_out') return 'checked-out';
  if (v === 'no_show') return 'no-show';
  return v || 'confirmed';
}

/** Backend reservation (arrivals/departures) → the UI's RoomBooking shape (fields the backend lacks default). */
function mapReservationToBooking(r: Raw): RoomBooking {
  const reservationId = str(r.id);
  return {
    id: legacyId(reservationId),
    reservation_id: reservationId,
    hold_id: str(r.holdId) || undefined,
    confirmation_number: str(r.confirmationNumber),
    guest_name: str(r.guestName) || '',
    guest_email: str(r.guestEmail) || '',
    guest_phone: str(r.guestPhone) || '',
    room_type_id: str(r.roomTypeId),
    room_type_name: str(r.roomTypeName) || '',
    room_number: (r.roomNumber as string) ?? null,
    assigned_room_id: (r.roomId as string) ?? null,
    check_in: str(r.checkIn),
    check_out: str(r.checkOut),
    check_in_time: str(r.checkInTime) || '14:00',
    check_out_time: str(r.checkOutTime) || '12:00',
    billing_start_time: str(r.billingStartTime) || '14:00',
    extended_checkout: Boolean(r.extendedCheckout),
    checkout_message_sent: Boolean(r.checkoutMessageSent),
    checkout_message_method: (r.checkoutMessageMethod as string) ?? null,
    guests: Number(r.adults ?? 0) + Number(r.children ?? 0),
    nights: Number(r.nights ?? 0),
    total_amount: fromMinorUnits(r.totalAmountMinorUnits as number),
    status: mapReservationStatus(r.status),
    special_requests: str(r.specialRequests),
    payment_status: str(r.paymentStatus) || 'pending',
    stripe_session_id: null,
    guest_nationality: null,
    guest_id_type: null,
    guest_id_number: null,
    amount_paid: fromMinorUnits(r.paidAmountMinorUnits as number),
    approval_status: null,
    booking_source: null,
    admin_notes: null,
    extra_charges_total: fromMinorUnits(r.extraChargesMinorUnits as number),
    created_at: str(r.createdAt),
  };
}

function mapFolioTransaction(f: Raw): GuestFolioCharge {
  const type = str(f.type);
  const settledByPaymentId = (f.settledByPaymentId as string | null) ?? null;
  return {
    id: str(f.id),
    booking_id: 0,
    type,
    charge_type: str(f.category) || type,
    description: str(f.description),
    amount: fromMinorUnits(f.amountMinorUnits as number),
    quantity: (f.quantity as number) ?? 1,
    posted_by: (f.postedBy as string) ?? null,
    posted_at: str(f.createdAt),
    payment_status:
      type === 'payment'
        ? 'paid'
        : type === 'credit'
        ? 'credited'
        : settledByPaymentId
        ? 'paid'
        : 'unpaid',
    settled_by_payment_id: type === 'charge' ? settledByPaymentId : null,
    created_at: str(f.createdAt),
    updated_at: str(f.createdAt),
  };
}

/** Backend room_change_history row → the UI's RoomChangeRecord. */
function mapRoomChangeRecord(c: Raw): RoomChangeRecord {
  const from = (c.fromRoomNumbers as string[]) ?? [];
  const to = (c.toRoomNumbers as string[]) ?? [];
  return {
    id: 0,
    booking_id: 0,
    previous_room_number: from.join(', ') || null,
    previous_room_id: null,
    new_room_number: to.join(', ') || null,
    new_room_id: null,
    previous_room_type: null,
    new_room_type: null,
    changed_by: str(c.performedBy),
    change_reason: (c.reason as string) ?? null,
    created_at: str(c.createdAt),
  };
}

export interface RoomBookingData {
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  roomTypeId: string;
  roomTypeName: string;
  roomNumber?: string;
  assignedRoomId?: string;
  checkIn: string;
  checkOut: string;
  checkInTime?: string;
  checkOutTime?: string;
  billingStartTime?: string;
  guests: number;
  nights: number;
  totalAmount: number;
  status?: string;
  specialRequests?: string;
  paymentStatus?: string;
  stripeSessionId?: string;
}

export interface PendingRoomReservation {
  holdId: string;
  confirmationNumber: string;
}

export interface PaymentInitialization {
  authorizationUrl: string;
  reference: string;
}

export type PublicPaymentStatus =
  | 'pending'
  | 'processing'
  | 'success'
  | 'failed'
  | 'abandoned'
  | 'refunded'
  | 'refund_pending'
  | 'refund_processing';

export interface PaymentStatusResult {
  reference: string;
  status: PublicPaymentStatus;
  purpose: 'room_reservation' | 'event_booking';
  paidAt: string | null;
}

export interface RoomBooking {
  id: number;
  /** Backend reservation UUID (arrivals/departures come from /reservations). Additive — the numeric `id` is unchanged. */
  reservation_id?: string;
  /** Inventory hold targeted by the Paystack payment record. */
  hold_id?: string;
  confirmation_number: string;
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  room_type_id: string;
  room_type_name: string;
  room_number: string | null;
  assigned_room_id: string | null;
  check_in: string;
  check_out: string;
  check_in_time: string;
  check_out_time: string;
  billing_start_time: string;
  extended_checkout: boolean;
  checkout_message_sent: boolean;
  checkout_message_method: string | null;
  guests: number;
  nights: number;
  total_amount: number;
  status: string;
  special_requests: string;
  payment_status: string;
  stripe_session_id: string | null;
  guest_nationality: string | null;
  guest_id_type: string | null;
  guest_id_number: string | null;
  amount_paid: number;
  approval_status: string | null;
  booking_source: string | null;
  admin_notes: string | null;
  extra_charges_total: number;
  extension_charge?: number;
  collected_amount?: number;
  created_at: string;
}

export interface GuestFolioCharge {
  id: string;
  booking_id: number;
  /** Ledger row kind: charge | payment | deposit | credit. */
  type: string;
  charge_type: string;
  description: string | null;
  amount: number;
  quantity: number;
  posted_by: string | null;
  posted_at: string;
  payment_status: string;
  /** Set on a charge row once a settlement payment covers it; null otherwise. */
  settled_by_payment_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface RoomChangeRecord {
  id: number;
  booking_id: number;
  previous_room_number: string | null;
  previous_room_id: string | null;
  new_room_number: string | null;
  new_room_id: string | null;
  previous_room_type: string | null;
  new_room_type: string | null;
  changed_by: string;
  change_reason: string | null;
  created_at: string;
}

export interface RatePlanRecord {
  id: string;
  name: string;
  plan_type: string;
  room_category_id: string | null;
  base_rate: number;
  weekend_rate: number | null;
  university_rate: number | null;
  seasonal_adjustment: number;
  valid_from: string | null;
  valid_to: string | null;
  auto_reserve: boolean;
  auto_reserve_room_count: number;
  min_stay: number;
  max_stay: number | null;
  advance_booking_days: number | null;
  non_refundable: boolean;
  status: string;
  applicable_room_types: string[];
  created_at: string;
  updated_at: string;
}

export interface RoomRecord {
  id: string;
  room_number: string;
  category_id: string;
  building: string;
  floor: string;
  occupancy_status: string;
  housekeeping_status: string;
  maintenance_status: string;
  notes: string | null;
  image_url: string | null;
}

export interface RoomCategoryRecord {
  id: string;
  name: string;
  description: string;
  max_occupancy: number;
  bed_type: string;
  public_rate: number;
  university_rate: number | null;
  amenities: string[];
  images: string[];
  /** Stored key per signed photo, aligned with `images`. */
  imageKeys: string[];
  size_sqm: number;
  status: string;
}

export interface RateAdjustmentQuote {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  impact: number;
}

export interface ReservationQuote {
  roomTypeId: string;
  currency: string;
  baseRate: number;
  adjustments: RateAdjustmentQuote[];
  adjustedRate: number;
  quantity: number;
  nights: number;
  lineTotal: number;
  vat: number;
  totalAmount: number;
}

export interface RoomInventoryStats {
  total: number;
  available: number;
  occupied: number;
  maintenance: number;
  dirty: number;
  byCategory: Record<string, { total: number; available: number; occupied: number }>;
  byFloor: Record<string, { total: number; available: number; occupied: number }>;
}

// ─── BACKEND RESPONSE SHAPES ──────────────────────────────────────────
type BackendRoomStatus =
  | 'available'
  | 'occupied'
  | 'maintenance'
  | 'cleaning'
  | 'out_of_order'
  | 'inactive';

interface BackendRoomType {
  id: string;
  name: string;
  description: string;
  bedType: string;
  maxOccupancy: number;
  baseRateMinorUnits: number;
  currency: string;
  amenities: string[];
  images: string[];
  imageKeys?: string[];
  sizeSqm: number;
}

interface BackendRoom {
  id: string;
  number: string;
  roomTypeId: string;
  roomType?: BackendRoomType | null;
  building: string;
  floor: number;
  status: BackendRoomStatus;
  lastCleanedAt: string | null;
  notes: string | null;
}

interface BackendRatePlan {
  id: string;
  name: string;
  roomTypeId: string;
  planType: string;
  baseRateMinorUnits: number;
  currency: string;
  minStayNights: number | null;
  maxStayNights: number | null;
  advanceBookingDays: number | null;
  nonRefundable: boolean;
  validFrom: string;
  validTo: string;
  isActive: boolean;
  autoReserve: boolean;
  autoReserveRoomCount: number;
}

interface BackendAvailability {
  roomTypeId: string;
  availableCount: number;
}

interface BackendMoney {
  minorUnits: number;
  currency: string;
}

interface BackendRateAdjustment {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  impact: BackendMoney;
}

interface BackendRateBreakdown {
  roomTypeId: string;
  baseRate: BackendMoney;
  adjustments: BackendRateAdjustment[];
  adjustedRate: BackendMoney;
  quantity: number;
  nights: number;
  lineTotal: BackendMoney;
  vat: BackendMoney;
  totalAmount: BackendMoney;
}

// ─── MAPPERS ──────────────────────────────────────────────────────────
function mapRoom(r: BackendRoom): RoomRecord {
  const imageUrl = r.roomType?.images?.[0] ?? null;
  return {
    id: r.id,
    room_number: r.number,
    category_id: r.roomTypeId,
    building: r.building ?? '',
    floor: String(r.floor ?? ''),
    occupancy_status: r.status === 'occupied' ? 'occupied' : 'available',
    // The API's `cleaning` state means the room is not yet ready for sale.
    // The operational screens call that state "dirty" until housekeeping
    // marks it clean, so keep one consistent UI vocabulary.
    housekeeping_status: r.status === 'cleaning' ? 'dirty' : 'clean',
    maintenance_status:
      r.status === 'maintenance' || r.status === 'out_of_order' || r.status === 'inactive'
        ? 'under_repair'
        : 'operational',
    notes: r.notes ?? null,
    image_url: imageUrl,
  };
}

function mapCategory(t: BackendRoomType): RoomCategoryRecord {
  const publicRate = fromMinorUnits(t.baseRateMinorUnits);
  return {
    id: t.id,
    name: t.name,
    description: t.description ?? '',
    max_occupancy: t.maxOccupancy ?? 0,
    bed_type: t.bedType ?? '',
    public_rate: publicRate,
    university_rate: null,
    amenities: t.amenities ?? [],
    images: t.images ?? [],
    imageKeys: t.imageKeys ?? [],
    size_sqm: t.sizeSqm ?? 0,
    status: 'active',
  };
}

function mapRatePlan(p: BackendRatePlan): RatePlanRecord {
  return {
    id: p.id,
    name: p.name,
    plan_type: p.planType ?? 'standard',
    room_category_id: p.roomTypeId ?? null,
    base_rate: fromMinorUnits(p.baseRateMinorUnits),
    weekend_rate: null,
    university_rate: null,
    seasonal_adjustment: 0,
    valid_from: p.validFrom ?? null,
    valid_to: p.validTo ?? null,
    auto_reserve: p.autoReserve,
    auto_reserve_room_count: p.autoReserveRoomCount,
    min_stay: p.minStayNights ?? 1,
    max_stay: p.maxStayNights ?? null,
    advance_booking_days: p.advanceBookingDays ?? null,
    non_refundable: !!p.nonRefundable,
    status: p.isActive ? 'active' : 'inactive',
    applicable_room_types: p.roomTypeId ? [p.roomTypeId] : [],
    created_at: '',
    updated_at: '',
  };
}

function mapReservationQuote(b: BackendRateBreakdown): ReservationQuote {
  return {
    roomTypeId: b.roomTypeId,
    currency: b.lineTotal.currency,
    baseRate: fromMinorUnits(b.baseRate.minorUnits),
    adjustments: (b.adjustments ?? []).map((a) => ({
      ruleId: a.ruleId,
      ruleName: a.ruleName,
      ruleType: a.ruleType,
      impact: fromMinorUnits(a.impact.minorUnits),
    })),
    adjustedRate: fromMinorUnits(b.adjustedRate.minorUnits),
    quantity: b.quantity,
    nights: b.nights,
    lineTotal: fromMinorUnits(b.lineTotal.minorUnits),
    vat: fromMinorUnits(b.vat.minorUnits),
    totalAmount: fromMinorUnits(b.totalAmount.minorUnits),
  };
}

function toDateStr(d: string | Date): string {
  return typeof d === 'string' ? d : d.toISOString().split('T')[0];
}

const bookingService = {
  /**
   * Fetch all room categories from /room-types.
   */
  async getRoomCategories(): Promise<RoomCategoryRecord[]> {
    const [types, plans] = await Promise.all([
      api.get<BackendRoomType[]>('/room-types', { auth: false }),
      api.get<BackendRatePlan[]>('/rate-plans', { auth: false }),
    ]);
    const planByRoomTypeId = new Map(
      (plans || [])
        .filter((plan) => plan.planType === 'university')
        .map((plan) => [plan.roomTypeId, plan]),
    );
    return (types || [])
      .map((type) => {
        const category = mapCategory(type);
        const universityPlan = planByRoomTypeId.get(type.id);
        if (universityPlan) {
          category.university_rate = fromMinorUnits(universityPlan.baseRateMinorUnits);
        }
        return category;
      })
      .sort((a: RoomCategoryRecord, b: RoomCategoryRecord) => a.public_rate - b.public_rate);
  },

  /**
   * Fetch all rooms from /rooms.
   */
  async getAllRooms(): Promise<RoomRecord[]> {
    const rooms = await api.get<BackendRoom[]>('/rooms');
    return (rooms || []).map(mapRoom);
  },

  /**
   * Fetch rooms by building and floor. The backend has no such filter, so both
   * are applied client-side.
   */
  async getRoomsByFloor(building: string, floor: string): Promise<RoomRecord[]> {
    const rooms = await api.get<BackendRoom[]>('/rooms');
    return (rooms || [])
      .map(mapRoom)
      .filter((r: RoomRecord) => r.building === building && r.floor === floor);
  },

  /**
   * Get the total room count for a given category (room type) ID.
   * Throws on failure rather than reporting an unreachable inventory as 0 rooms.
   */
  async getTotalRoomsOfType(categoryId: string): Promise<number> {
    const rooms = await api.get<BackendRoom[]>('/rooms');
    return (rooms || []).filter((r: BackendRoom) => r.roomTypeId === categoryId).length;
  },

  /**
   * Check how many rooms of a given category are available for a date range.
   * Throws on failure — a failed check must never be reported to callers as 0,
   * which they render as "sold out".
   */
  async checkTypeAvailability(
    categoryId: string,
    checkIn: string | Date,
    checkOut: string | Date
  ): Promise<number> {
    const res = await api.get<BackendAvailability>('/availability', {
      auth: false,
      query: {
        roomTypeId: categoryId,
        checkIn: toDateStr(checkIn),
        checkOut: toDateStr(checkOut),
      },
    });
    if (typeof res?.availableCount !== 'number') {
      throw new Error(`Malformed availability response for room type ${categoryId}`);
    }
    return res.availableCount;
  },

  /**
   * Find the first sellable room of the given category for the date range.
   * Backend availability returns a count only, so this returns the first
   * available room of that type from /rooms.
   */
  async findAvailableRoom(
    categoryId: string,
    checkIn: string | Date,
    checkOut: string | Date
  ): Promise<RoomRecord | null> {
    const rooms = await this.getAvailableRoomsForType(categoryId, checkIn, checkOut);
    return rooms[0] ?? null;
  },

  async getAvailableRoomsForType(
    categoryId: string,
    checkIn: string | Date,
    checkOut: string | Date,
  ): Promise<RoomRecord[]> {
    const rooms = await api.get<BackendRoom[]>('/availability/rooms', {
      query: {
        roomTypeId: categoryId,
        checkIn: toDateStr(checkIn),
        checkOut: toDateStr(checkOut),
      },
    });
    return rooms.map(mapRoom);
  },

  /**
   * Rooms a reservation can move to: the room it is in now, then every other
   * room of the same type that is free for the stay. The current room is not
   * in the availability result, because its own stay occupies it.
   */
  async getRoomsForReassignment(input: {
    roomTypeId: string;
    checkIn: string;
    checkOut: string;
    currentRoomId?: string | null;
  }): Promise<RoomRecord[]> {
    const available = (
      await this.getAvailableRoomsForType(input.roomTypeId, input.checkIn, input.checkOut)
    ).filter((room) => room.id !== input.currentRoomId);
    available.sort((a, b) =>
      a.room_number.localeCompare(b.room_number, undefined, { numeric: true }),
    );
    if (!input.currentRoomId) return available;
    const current = (await this.getAllRooms()).find((room) => room.id === input.currentRoomId);
    return current ? [current, ...available] : available;
  },

  /**
   * Check solo booking availability.
   */
  async checkSoloAvailability(
    categoryId: string,
    checkIn: string | Date,
    checkOut: string | Date
  ): Promise<{ available: boolean; availableCount: number }> {
    const count = await this.checkTypeAvailability(categoryId, checkIn, checkOut);
    return { available: count > 0, availableCount: count };
  },

  /**
   * Check group booking availability.
   */
  async checkGroupAvailability(
    allocations: { typeId: string; typeName: string; count: number }[],
    checkIn: string | Date,
    checkOut: string | Date
  ): Promise<Array<{ typeId: string; typeName: string; requested: number; available: boolean; availableCount: number }>> {
    const results = await Promise.all(
      allocations.map(async (alloc) => {
        const availableCount = await this.checkTypeAvailability(alloc.typeId, checkIn, checkOut);
        return {
          typeId: alloc.typeId,
          typeName: alloc.typeName,
          requested: alloc.count,
          available: availableCount >= alloc.count,
          availableCount,
        };
      })
    );
    return results;
  },

  /**
   * Get available count for ALL room categories for a date range.
   * A category whose check fails is omitted rather than recorded as 0, so one
   * bad type degrades to "unknown" instead of blanking the whole search page.
   * Callers must treat a missing key as unknown — never as sold out.
   */
  async getAllTypeAvailability(
    checkIn: string | Date,
    checkOut: string | Date
  ): Promise<Record<string, number>> {
    const categories = await this.getRoomCategories();
    const result: Record<string, number> = {};
    await Promise.all(
      categories.map(async (cat) => {
        try {
          result[cat.id] = await this.checkTypeAvailability(cat.id, checkIn, checkOut);
        } catch (e) {
          console.error(`[bookingService] Availability check failed for ${cat.id}:`, e);
        }
      })
    );
    return result;
  },

  /**
   * Get total available rooms across all categories.
   */
  async getTotalAvailableCount(
    checkIn: string | Date,
    checkOut: string | Date
  ): Promise<number> {
    const availability = await this.getAllTypeAvailability(checkIn, checkOut);
    return Object.values(availability).reduce((sum, count) => sum + count, 0);
  },

  /**
   * Create a room booking.
   */
  async quoteReservation(params: {
    roomTypeId: string;
    checkIn: string | Date;
    checkOut: string | Date;
    quantity?: number;
    corporateTierRank?: number;
  }): Promise<ReservationQuote> {
    const breakdown = await api.post<BackendRateBreakdown>(
      '/reservations/quote',
      {
        roomTypeId: params.roomTypeId,
        checkIn: toDateStr(params.checkIn),
        checkOut: toDateStr(params.checkOut),
        quantity: params.quantity ?? 1,
        corporateTierRank: params.corporateTierRank,
      },
      { auth: false },
    );
    if (typeof breakdown?.totalAmount?.minorUnits !== 'number') {
      throw new Error(`Malformed quote response for room type ${params.roomTypeId}`);
    }
    return mapReservationQuote(breakdown);
  },

  async createRoomBooking(data: RoomBookingData): Promise<RoomBooking> {
    const [firstName, ...rest] = data.guestName.trim().split(/\s+/);
    const response = await api.post<{ reservations: Raw[] }>('/reservations', {
      roomTypeId: data.roomTypeId,
      checkIn: data.checkIn,
      checkOut: data.checkOut,
      quantity: 1,
      adults: Math.max(1, data.guests),
      children: 0,
      guest: {
        firstName: firstName || 'Guest',
        lastName: rest.join(' ') || 'Guest',
        email: data.guestEmail,
        phone: data.guestPhone,
      },
      specialRequests: data.specialRequests || undefined,
    });
    const raw = response.reservations[0];
    if (!raw) throw new Error('The server did not return the created reservation');
    return {
      ...mapReservationToBooking(raw),
      guest_name: data.guestName,
      guest_email: data.guestEmail,
      guest_phone: data.guestPhone,
      room_type_name: data.roomTypeName,
    };
  },

  /**
   * Create the pending reservations and inventory holds that an online payment
   * payment will settle. Mixed room types require one backend request per
   * type; if a later type fails, earlier holds are cancelled before the error
   * is returned so a partial group booking is never left behind.
   */
  async createPendingRoomReservations(data: {
    allocations: Array<{
      roomTypeId: string;
      quantity: number;
      adultsPerRoom: number;
    }>;
    checkIn: string;
    checkOut: string;
    guest: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    specialRequests?: string;
  }): Promise<PendingRoomReservation[]> {
    const created: PendingRoomReservation[] = [];

    try {
      for (const allocation of data.allocations) {
        const response = await api.post<{ reservations: Raw[] }>(
          '/reservations',
          {
            roomTypeId: allocation.roomTypeId,
            checkIn: data.checkIn,
            checkOut: data.checkOut,
            quantity: allocation.quantity,
            adults: Math.max(1, allocation.adultsPerRoom),
            children: 0,
            guest: data.guest,
            specialRequests: data.specialRequests || undefined,
          },
          { auth: false },
        );

        for (const reservation of response.reservations) {
          const holdId = str(reservation.holdId);
          const confirmationNumber = str(reservation.confirmationNumber);
          if (!holdId || !confirmationNumber) {
            throw new Error('The server returned an incomplete reservation');
          }
          created.push({ holdId, confirmationNumber });
        }
      }
      return created;
    } catch (error) {
      await Promise.allSettled(
        created.map((reservation) =>
          this.cancelPendingReservation(
            reservation.confirmationNumber,
            data.guest.email,
          ),
        ),
      );
      throw error;
    }
  },

  async initializeRoomPayment(data: {
    holdIds: string[];
    email: string;
    callbackUrl: string;
  }): Promise<PaymentInitialization> {
    return api.post<PaymentInitialization>(
      '/payments/initialize/room-reservation',
      data,
      { auth: false },
    );
  },

  async getPaymentStatus(
    reference: string,
    email: string,
  ): Promise<PaymentStatusResult> {
    return api.get<PaymentStatusResult>(
      `/payments/status/${encodeURIComponent(reference)}`,
      { auth: false, query: { email } },
    );
  },

  async cancelPendingReservation(
    confirmationNumber: string,
    email: string,
  ): Promise<void> {
    await api.post(
      `/reservations/confirmation/${encodeURIComponent(confirmationNumber)}/cancel`,
      { email },
      { auth: false },
    );
  },

  /**
   * Create multiple bookings at once.
   */
  async createMultipleBookings(bookings: RoomBookingData[]): Promise<RoomBooking[]> {
    const created: RoomBooking[] = [];
    for (const booking of bookings) created.push(await this.createRoomBooking(booking));
    return created;
  },

  /**
   * Check in a guest.
   */
  async checkInRoom(bookingId: number, _assignedRoomId: string): Promise<void> {
    await api.post(`/reservations/${reservationIdFor(bookingId)}/check-in`);
    toast.success('Guest checked in');
  },

  /**
   * Check out a guest.
   */
  /**
   * A checkout past the property's check-out time is refused with a 409 until
   * `acceptLateFee` is set — that acceptance is what posts the fee to the folio.
   */
  async checkOutRoom(
    bookingId: number,
    _assignedRoomId: string,
    options: { acceptLateFee?: boolean } = {},
  ): Promise<{ lateCheckout: boolean; lateCheckoutFeeMinorUnits: number }> {
    const result = await api.post<{ lateCheckout?: boolean; lateCheckoutFeeMinorUnits?: number }>(
      `/reservations/${reservationIdFor(bookingId)}/check-out`,
      { acceptLateFee: options.acceptLateFee ?? false },
    );
    toast.success('Guest checked out');
    return {
      lateCheckout: result?.lateCheckout ?? false,
      lateCheckoutFeeMinorUnits: result?.lateCheckoutFeeMinorUnits ?? 0,
    };
  },

  /**
   * Cancel a booking.
   */
  async cancelBooking(bookingId: number, _assignedRoomId: string | null, guestEmail?: string): Promise<void> {
    if (guestEmail) {
      const booking = await this.getBookingById(bookingId);
      if (!booking) throw new Error('Reservation not found');
      await api.post(
        `/reservations/confirmation/${booking.confirmation_number}/cancel`,
        { email: guestEmail },
        { auth: false },
      );
    } else {
      await api.post(`/reservations/${reservationIdFor(bookingId)}/cancel`);
    }
    toast.success('Reservation cancelled');
  },

  /**
   * Update a room's housekeeping status via PATCH /rooms/:id/status.
   */
  async updateHousekeepingStatus(roomId: string, status: string): Promise<void> {
    const backendStatus: BackendRoomStatus =
      status === 'dirty' || status === 'cleaning' ? 'cleaning' : 'available';
    try {
      await api.patch(`/rooms/${roomId}/status`, { status: backendStatus });
      toast.success('Housekeeping status updated');
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  },

  /**
   * Update a room's maintenance status via PATCH /rooms/:id/status.
   */
  async updateMaintenanceStatus(roomId: string, status: string, notes?: string): Promise<void> {
    let backendStatus: BackendRoomStatus;
    if (status === 'operational') backendStatus = 'available';
    else if (status === 'out_of_order') backendStatus = 'out_of_order';
    else if (status === 'inactive') backendStatus = 'inactive';
    else backendStatus = 'maintenance';
    try {
      await api.patch(`/rooms/${roomId}/status`, {
        status: backendStatus,
        ...(notes !== undefined ? { notes } : {}),
      });
      toast.success('Maintenance status updated');
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  },

  /**
   * Update a room's occupancy status via PATCH /rooms/:id/status.
   */
  async updateOccupancyStatus(roomId: string, status: string): Promise<void> {
    const backendStatus: BackendRoomStatus =
      status === 'occupied' ? 'occupied' : 'available';
    try {
      await api.patch(`/rooms/${roomId}/status`, { status: backendStatus });
      toast.success('Room status updated');
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  },

  /**
   * Get comprehensive room inventory stats, derived from /rooms.
   */
  async getRoomInventoryStats(): Promise<RoomInventoryStats> {
    const rooms = await api.get<BackendRoom[]>('/rooms');
    const roomList = (rooms || []).map(mapRoom);
    const categoryNames = new Map(
      (rooms || []).map((room) => [room.roomTypeId, room.roomType?.name]),
    );

    const byCategory: RoomInventoryStats['byCategory'] = {};
    const byFloor: RoomInventoryStats['byFloor'] = {};

    let available = 0;
    let occupied = 0;
    let maintenance = 0;
    let dirty = 0;

    for (const r of roomList) {
      const displayCategory = categoryNames.get(r.category_id) || r.category_id;
      if (!byCategory[displayCategory]) {
        byCategory[displayCategory] = { total: 0, available: 0, occupied: 0 };
      }
      byCategory[displayCategory].total++;

      const floorKey = `${r.building} - ${r.floor}`;
      if (!byFloor[floorKey]) {
        byFloor[floorKey] = { total: 0, available: 0, occupied: 0 };
      }
      byFloor[floorKey].total++;

      if (r.maintenance_status !== 'operational') {
        maintenance++;
        continue;
      }

      if (r.occupancy_status === 'occupied') {
        occupied++;
        byCategory[displayCategory].occupied++;
        byFloor[floorKey].occupied++;
      } else if (r.occupancy_status === 'available') {
        if (r.housekeeping_status === 'dirty') {
          dirty++;
        } else {
          available++;
          byCategory[displayCategory].available++;
          byFloor[floorKey].available++;
        }
      }
    }

    return { total: roomList.length, available, occupied, maintenance, dirty, byCategory, byFloor };
  },

  /**
   * Get today's arrivals (check-ins).
   */
  async getTodaysArrivals(): Promise<RoomBooking[]> {
    const today = new Date().toISOString().split('T')[0];
    const data = await api.get<Raw[]>('/reservations/arrivals', { query: { date: today } });
    return data.map(mapReservationToBooking);
  },

  /**
   * Get today's departures (check-outs).
   */
  async getTodaysDepartures(): Promise<RoomBooking[]> {
    const today = new Date().toISOString().split('T')[0];
    const data = await api.get<Raw[]>('/reservations/departures', { query: { date: today } });
    return data.map(mapReservationToBooking);
  },

  // ─── Reservation folio / room-history / reassign (backend: /reservations/:id/*) ───

  /** Folio ledger for a reservation (reservationId = backend UUID, e.g. from arrivals). */
  async getReservationFolio(reservationId: string): Promise<GuestFolioCharge[]> {
    const data = await api.get<Raw[]>(`/reservations/${reservationId}/folio`);
    return data.map(mapFolioTransaction);
  },

  async getReservationFolioSummary(reservationId: string): Promise<{ totalCharges: number; totalPayments: number; totalDeposits: number; balance: number }> {
    const r = await api.get<Raw>(`/reservations/${reservationId}/folio/summary`);
    return {
      totalCharges: fromMinorUnits(r.totalChargesMinorUnits as number),
      totalPayments: fromMinorUnits(r.totalPaymentsMinorUnits as number),
      totalDeposits: fromMinorUnits(r.totalDepositsMinorUnits as number),
      balance: fromMinorUnits(r.balanceMinorUnits as number),
    };
  },

  async addReservationFolioCharge(reservationId: string, charge: { description: string; category?: string; amount: number; guestName?: string; roomNumber?: string; reference?: string }): Promise<boolean> {
    try {
      await api.post(`/reservations/${reservationId}/folio/charge`, {
        description: charge.description,
        category: charge.category,
        amountMinorUnits: toMinorUnits(charge.amount),
        guestName: charge.guestName,
        roomNumber: charge.roomNumber,
        reference: charge.reference,
      });
      toast.success('Charge posted');
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  },

  async addReservationFolioPayment(reservationId: string, payment: { amount: number; method: string; reference?: string; notes?: string }): Promise<boolean> {
    try {
      await api.post(`/reservations/${reservationId}/folio/payment`, {
        amountMinorUnits: toMinorUnits(payment.amount),
        method: payment.method,
        reference: payment.reference,
        notes: payment.notes,
      });
      toast.success('Payment posted');
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  },

  /**
   * Settles specific unpaid charges in one payment. The amount is never
   * sent — the backend computes it from the charges themselves, so what
   * gets recorded can never be less than what was actually owed.
   */
  async settleReservationFolioCharges(
    bookingId: number,
    settlement: { chargeIds: string[]; method: string; reference?: string; notes?: string },
  ): Promise<boolean> {
    try {
      await api.post(`/reservations/${reservationIdFor(bookingId)}/folio/settle`, {
        chargeIds: settlement.chargeIds,
        method: settlement.method,
        reference: settlement.reference,
        notes: settlement.notes,
      });
      toast.success('Payment recorded');
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  },

  async addReservationFolioDeposit(reservationId: string, amount: number): Promise<boolean> {
    try {
      await api.post(`/reservations/${reservationId}/folio/deposit`, { amountMinorUnits: toMinorUnits(amount) });
      toast.success('Deposit posted');
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  },

  /** Move a reservation to a different room (roomId = backend room UUID). Logs to room-change history. */
  async reassignReservationRoom(reservationId: string, roomId: string, reason?: string): Promise<boolean> {
    try {
      await api.post(`/reservations/${reservationId}/reassign-room`, { roomId, reason });
      toast.success('Room reassigned');
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  },

  async getReservationRoomHistory(reservationId: string): Promise<RoomChangeRecord[]> {
    const data = await api.get<Raw[]>(`/reservations/${reservationId}/room-history`);
    return data.map(mapRoomChangeRecord);
  },

  /**
   * Get all active bookings.
   */
  async getAllBookings(): Promise<RoomBooking[]> {
    const data = await api.get<Raw[]>('/reservations');
    return data.map(mapReservationToBooking);
  },

  /**
   * Get bookings for a date range.
   */
  async getBookingsByDateRange(start: string, end: string): Promise<RoomBooking[]> {
    const bookings = await this.getAllBookings();
    return bookings.filter((booking) => booking.check_in <= end && booking.check_out >= start);
  },

  /**
   * Get booked date ranges for a room category.
   */
  async getBookedDatesForType(
    categoryId: string,
    fromDate: string,
    toDate: string
  ): Promise<Array<{ check_in: string; check_out: string }>> {
    const bookings = await this.getBookingsByDateRange(fromDate, toDate);
    return bookings
      .filter((booking) => booking.room_type_id === categoryId && !['cancelled', 'no-show'].includes(booking.status))
      .map(({ check_in, check_out }) => ({ check_in, check_out }));
  },

  /**
   * Get a single room booking by ID.
   */
  async getBookingById(bookingId: number): Promise<RoomBooking | null> {
    const knownId = reservationIds.get(bookingId);
    if (knownId) {
      return mapReservationToBooking(await api.get<Raw>(`/reservations/${knownId}`));
    }
    return (await this.getAllBookings()).find((booking) => booking.id === bookingId) ?? null;
  },

  /**
   * Get a booking by confirmation number.
   */
  async getBookingByConfirmation(confirmationNumber: string, guestEmail?: string): Promise<RoomBooking | null> {
    if (guestEmail) {
      const data = await api.get<Raw[]>(`/reservations/confirmation/${encodeURIComponent(confirmationNumber)}`, {
        auth: false,
        query: { email: guestEmail },
      });
      return data[0] ? mapReservationToBooking(data[0]) : null;
    }
    return (await this.getAllBookings()).find(
      (booking) => booking.confirmation_number === confirmationNumber,
    ) ?? null;
  },

  /**
   * Update a booking's guest information and details.
   */
  async updateBookingDetails(
    bookingId: number,
    updates: {
      guestName?: string;
      guestEmail?: string;
      guestPhone?: string;
      checkIn?: string;
      checkOut?: string;
      checkInTime?: string;
      checkOutTime?: string;
      guests?: number;
      specialRequests?: string;
    }
  ): Promise<RoomBooking | null> {
    const updated = await api.patch<Raw>(
      `/reservations/${reservationIdFor(bookingId)}`,
      updates,
    );
    return mapReservationToBooking(updated);
  },

  /**
   * Get room type name from /room-types/:id.
   */
  async getRoomTypeName(categoryId: string): Promise<string> {
    const type = await api.get<BackendRoomType>(`/room-types/${categoryId}`, { auth: false });
    return type?.name || 'Unknown Room';
  },

  /**
   * Get all room bookings for a specific room.
   */
  async getBookingsForRoom(roomId: string): Promise<RoomBooking[]> {
    return (await this.getAllBookings()).filter((booking) => booking.assigned_room_id === roomId);
  },

  // ─── FLEXIBLE DATES & TIMES ──────────────────────────────────────

  /**
   * Update reservation dates and times.
   */
  async updateReservationDates(
    bookingId: number,
    updates: {
      checkIn?: string;
      checkOut?: string;
      checkInTime?: string;
      checkOutTime?: string;
      billingStartTime?: string;
    }
  ): Promise<RoomBooking | null> {
    const updated = await api.patch<Raw>(
      `/reservations/${reservationIdFor(bookingId)}`,
      updates,
    );
    return mapReservationToBooking(updated);
  },

  /**
   * Extend checkout by 1 hour.
   */
  async extendCheckout(bookingId: number, currentBooking: RoomBooking): Promise<RoomBooking | null> {
    const [hours, minutes] = currentBooking.check_out_time.split(':').map(Number);
    const nextMinutes = Math.min(23 * 60 + 59, hours * 60 + minutes + 60);
    return this.updateReservationDates(bookingId, {
      checkOutTime: `${String(Math.floor(nextMinutes / 60)).padStart(2, '0')}:${String(nextMinutes % 60).padStart(2, '0')}`,
    });
  },

  // ─── CHECKOUT MESSAGES ────────────────────────────────────────────

  /**
   * Extend a guest's stay.
   */
  async extendStay(
    bookingId: number,
    newCheckOut: string,
    _additionalNights: number,
    _additionalAmount: number,
    paymentMethod: string,
    paymentReference?: string
  ): Promise<RoomBooking | null> {
    const reservationId = reservationIdFor(bookingId);
    const updated = await api.post<Raw>(
      `/reservations/${reservationId}/extend-stay`,
      {
        newCheckOut,
        paymentMethod,
        paymentReference,
      },
    );
    return {
      ...mapReservationToBooking(updated),
      extension_charge: fromMinorUnits(updated.extensionChargeMinorUnits as number),
      collected_amount: fromMinorUnits(updated.collectedAmountMinorUnits as number),
    };
  },

  /**
   * Send customized checkout message to guest.
   */
  async sendCheckoutMessage(
    bookingId: number,
    method: 'email' | 'phone' | 'both',
    customMessage?: string
  ): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>(
      `/reservations/${reservationIdFor(bookingId)}/checkout-message`,
      { method, customMessage },
    );
  },

  // ─── ROOM CHANGE HISTORY ──────────────────────────────────────────

  /**
   * Change room assignment for a booking.
   */
  async changeRoomAssignment(
    bookingId: number,
    _newRoomNumber: string,
    newRoomId: string,
    _newRoomType: string,
    _changedBy: string,
    reason?: string
  ): Promise<{ success: boolean; booking: RoomBooking | null }> {
    const success = await this.reassignReservationRoom(
      reservationIdFor(bookingId),
      newRoomId,
      reason,
    );
    return {
      success,
      booking: success ? await this.getBookingById(bookingId) : null,
    };
  },

  /**
   * Get active booking for a specific room.
   */
  async getActiveBookingForRoom(_roomId: string): Promise<RoomBooking | null> {
    return (await this.getBookingsForRoom(_roomId)).find(
      (booking) => ['confirmed', 'checked-in'].includes(booking.status),
    ) ?? null;
  },

  /**
   * Get room change history for a booking.
   */
  async getRoomChangeHistory(bookingId: number): Promise<RoomChangeRecord[]> {
    return this.getReservationRoomHistory(reservationIdFor(bookingId));
  },

  /**
   * Get all previous reservations for the same guest.
   */
  async getPreviousReservations(guestEmail: string, excludeBookingId?: number): Promise<RoomBooking[]> {
    return (await this.getAllBookings()).filter(
      (booking) => booking.guest_email.toLowerCase() === guestEmail.toLowerCase()
        && booking.id !== excludeBookingId,
    );
  },

  // ─── GUEST FOLIO CHARGES ──────────────────────────────────────────

  /**
   * Get all folio charges for a booking.
   */
  async getFolioCharges(bookingId: number): Promise<GuestFolioCharge[]> {
    return this.getReservationFolio(reservationIdFor(bookingId));
  },

  /**
   * Add a service charge to a guest's folio.
   */
  async addFolioCharge(
    bookingId: number,
    chargeType: string,
    description: string | null,
    amount: number,
    quantity: number,
    _postedBy: string
  ): Promise<GuestFolioCharge | null> {
    const created = await api.post<Raw>(
      `/reservations/${reservationIdFor(bookingId)}/folio/charge`,
      {
        description: description?.trim() || chargeType,
        category: chargeType,
        amountMinorUnits: toMinorUnits(amount * Math.max(1, quantity)),
        quantity: Math.max(1, quantity),
      },
    );
    return mapFolioTransaction(created);
  },

  /**
   * Remove a folio charge.
   */
  async removeFolioCharge(chargeId: string, bookingId: number): Promise<boolean> {
    await api.post(
      `/reservations/${reservationIdFor(bookingId)}/folio/${chargeId}/void`,
    );
    return true;
  },

  /**
   * Folio statement PDF for a booking.
   */
  async getFolioStatementPdf(bookingId: number): Promise<Blob> {
    return api.getBlob(`/reservations/${reservationIdFor(bookingId)}/folio/pdf`);
  },

  /**
   * Email the folio statement PDF to the guest.
   */
  async emailFolioStatement(bookingId: number): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>(
      `/reservations/${reservationIdFor(bookingId)}/folio/email`,
    );
  },

  /**
   * Record a payment against a booking.
   */
  async recordPayment(
    bookingId: number,
    amount: number,
    method: string,
    _totalAmount: number,
    options?: { reference?: string; notes?: string }
  ): Promise<RoomBooking | null> {
    const updated = await api.post<Raw>(
      `/reservations/${reservationIdFor(bookingId)}/manual-payment`,
      {
        amountMinorUnits: toMinorUnits(amount),
        method,
        reference: options?.reference?.trim() || undefined,
        notes: options?.notes?.trim() || undefined,
      },
    );
    return mapReservationToBooking(updated);
  },

  /**
   * Get grand total for a booking (room cost + extra charges - amount paid).
   */
  async getBookingGrandTotal(bookingId: number): Promise<{ roomTotal: number; extraCharges: number; amountPaid: number; outstanding: number }> {
    const booking = await this.getBookingById(bookingId);
    if (!booking) return { roomTotal: 0, extraCharges: 0, amountPaid: 0, outstanding: 0 };

    const extraCharges = booking.extra_charges_total || 0;
    const amountPaid = booking.amount_paid || 0;
    const roomTotal = booking.total_amount || 0;
    const outstanding = roomTotal + extraCharges - amountPaid;

    return { roomTotal, extraCharges, amountPaid, outstanding };
  },

  // ─── RATE PLANS ───────────────────────────────────────────────────

  /**
   * Fetch all rate plans from /rate-plans.
   */
  async getRatePlans(): Promise<RatePlanRecord[]> {
    const plans = await api.get<BackendRatePlan[]>('/rate-plans', { auth: false });
    return (plans || []).map(mapRatePlan);
  },

  /**
   * Fetch active rate plans for a specific date + room category.
   * Backend has no date filter, so filtering is done client-side.
   */
  async getActiveRatePlansForDate(date: string, categoryId?: string): Promise<RatePlanRecord[]> {
    const plans = await api.get<BackendRatePlan[]>('/rate-plans', {
      auth: false,
      query: categoryId ? { roomTypeId: categoryId } : undefined,
    });
    return (plans || [])
      .map(mapRatePlan)
      .filter(
        (p: RatePlanRecord) =>
          p.status === 'active' &&
          (!p.valid_from || p.valid_from <= date) &&
          (!p.valid_to || p.valid_to >= date)
      )
      .sort((a: RatePlanRecord, b: RatePlanRecord) => a.base_rate - b.base_rate);
  },

  /**
   * Create a new rate plan via POST /rate-plans.
   */
  async createRatePlan(plan: Partial<RatePlanRecord>): Promise<RatePlanRecord | null> {
    try {
      const created = await api.post<BackendRatePlan>('/rate-plans', {
        name: plan.name,
        planType: plan.plan_type,
        roomTypeId: plan.room_category_id || plan.applicable_room_types?.[0] || '',
        baseRateMinorUnits: toMinorUnits(plan.base_rate || 0),
        minStayNights: plan.min_stay || undefined,
        maxStayNights: plan.max_stay || undefined,
        advanceBookingDays: plan.advance_booking_days || undefined,
        nonRefundable: plan.non_refundable || false,
        validFrom: plan.valid_from || undefined,
        validTo: plan.valid_to || undefined,
      });
      toast.success('Rate plan created');
      return mapRatePlan(created);
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    }
  },

  /**
   * Update an existing rate plan.
   */
  async updateRatePlan(planId: string, updates: Partial<RatePlanRecord>): Promise<RatePlanRecord | null> {
    const updated = await api.patch<BackendRatePlan>(`/rate-plans/${planId}`, {
      name: updates.name,
      planType: updates.plan_type,
      roomTypeId: updates.room_category_id ?? undefined,
      baseRateMinorUnits:
        updates.base_rate === undefined ? undefined : toMinorUnits(updates.base_rate),
      minStayNights: updates.min_stay,
      maxStayNights: updates.max_stay ?? undefined,
      advanceBookingDays: updates.advance_booking_days ?? undefined,
      nonRefundable: updates.non_refundable,
      validFrom: updates.valid_from ?? undefined,
      validTo: updates.valid_to ?? undefined,
      autoReserve: updates.auto_reserve,
      autoReserveRoomCount: updates.auto_reserve_room_count,
    });
    toast.success('Rate plan updated');
    return mapRatePlan(updated);
  },

  /**
   * Toggle auto-reserve on a rate plan.
   */
  async toggleAutoReserve(planId: string, enabled: boolean, roomCount = 0): Promise<RatePlanRecord | null> {
    return this.updateRatePlan(planId, {
      auto_reserve: enabled,
      auto_reserve_room_count: enabled ? roomCount : 0,
    });
  },

  /**
   * Delete a rate plan.
   */
  async deleteRatePlan(planId: string): Promise<boolean> {
    await api.del(`/rate-plans/${planId}`);
    toast.success('Rate plan archived');
    return true;
  },

};

export default bookingService;
